import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import {
  getLocalStorage,
  getSessionStorage,
  setLocalStorage,
  setSessionStorage,
} from 'src/functions/localStorageHandler';
import { StatsigClient } from '@statsig/js-client';
import { StatsigContext, StatsigProvider } from '@statsig/react-bindings';
import { v4 } from 'uuid';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';
import {
  CONSENT_CATEGORIES,
  CONSENT_MODELS,
  bannerAnsweredBefore,
  categoryActive,
  onConsentChanged,
  onConsentDecided,
  onConsentGranted,
  onConsentModelKnown,
} from 'src/functions/consent/oneTrustConsent';
import { globalPrivacyControl } from 'src/functions/consent/globalPrivacyControl';
import { setGA4TagUserId } from 'src/functions/analytics/ga4Tag';
import {
  holdStatsigEvents,
  markStatsigAwaitingConsent,
  markStatsigNoClient,
  releaseStatsigEvents,
  setStatsigClient,
} from './statsigClient';
import { logPageLandedOncePerSession, snapshotLandingOncePerSession } from './pageLanded';

// Longest a returning visitor's experiments wait for OneTrust to report their
// earlier choice before resolving to their defaults (OneTrust may be blocked).
// For a first-time visitor it bounds, from mount, the wait for OneTrust's
// consent model when the window load or OneTrust never arrives. Once a client
// exists, its initialize (CLIENT_INIT_TIMEOUT_MS in statsigClient.ts) can then
// hold the page for a further wait of its own.
const CONSENT_DECISION_TIMEOUT_MS = 5000;

// Longest a first-time visitor's experiments wait, after the window has
// loaded, for OneTrust to report the consent model before resolving to their
// defaults. This is the usual bound on that wait; CONSENT_DECISION_TIMEOUT_MS
// covers a window that never finishes loading. The script loader injects OneTrust on window load, so this measures
// OneTrust's response time. It normally reports within a few hundred
// milliseconds; the cap bounds the blank page when it is blocked.
const FIRST_VISIT_DECISION_TIMEOUT_MS = 2000;

const isScriptless = process.env.GATSBY_SITE_ENV === 'scriptless';

// A layout effect runs before the passive effects of the children, so the
// awaiting-consent status is set before any experiment hook first reads it.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const FIRST_TOUCH_TEXT = 'first time visitor';

declare global {
  interface Window {
    dataLayer?: Record<string, any>[];
  }
}

// The GA4 tag takes `user_id` directly. The same id also goes to the GTM
// dataLayer as a Custom Event, which a push without an `event` key cannot
// fire, with `user_id` a top-level key for GTM's Data Layer Variable.
const GA4_USER_ID_EVENT = 'statsig_user_id_ready';

// Each mount pushes the ID at most once, but every push would fire that
// trigger again, so a remounted provider does not push an ID this page
// already has.
let lastPushedGA4UserId: string | null = null;

const setGA4UserId = (userId: string): void => {
  if (typeof window === 'undefined' || !userId || userId === lastPushedGA4UserId) return;

  if (window.dataLayer) {
    setGA4TagUserId(userId);
    window.dataLayer.push({ event: GA4_USER_ID_EVENT, user_id: userId });
    lastPushedGA4UserId = userId;
  }
};

interface VisitedPath {
  propertyName: string;
  propertyValue: string;
}

const VISITED_PATHS_TO_TRACK: Record<string, VisitedPath> = {
  '/reviews': {
    propertyName: 'visitedReviews',
    propertyValue: 'viewed Reviews page',
  },
  '/pro': {
    propertyName: 'visitedPro',
    propertyValue: 'viewed Pro page',
  },
  '/request-a-call-form': {
    propertyName: 'visitedRequestACallForm',
    propertyValue: 'viewed Request a Call Form page',
  },
};

const getCurrentPathTrackCustomProperty = (path: string) => {
  if (!path) return {};

  if (VISITED_PATHS_TO_TRACK[path]) {
    const { propertyName, propertyValue } = VISITED_PATHS_TO_TRACK[path];

    const visitedPath = {
      [propertyName]: propertyValue,
    };

    setLocalStorage(`visitedPath:${propertyName}`, JSON.stringify(visitedPath));

    return visitedPath;
  }

  return {};
};

const getPersistedPathTrackCustomProperty = () => {
  const visitedPaths: Record<string, string> = {};

  Object.values(VISITED_PATHS_TO_TRACK).forEach(({ propertyName }) => {
    const visitedPathFromStorage = getLocalStorage(`visitedPath:${propertyName}`);

    if (!visitedPathFromStorage) return;

    const { [propertyName]: propertyValue } = JSON.parse(visitedPathFromStorage);

    visitedPaths[propertyName] = propertyValue;
  });

  return visitedPaths;
};

// Builds the visitor's Statsig identity (a persisted anonymous ID plus the
// pages they have visited) and creates the client with it. With `holdEvents`
// the client still fetches the visitor's assignments, so experiments render,
// but it keeps exposures and events on the device until
// `releaseStatsigEvents`; the GA4 user ID waits with them.
const createStatsigClient = ({ holdEvents = false } = {}): { client: StatsigClient; userId: string } => {
  const statsigUserId = getLocalStorage('statsigUserId');
  let userId = statsigUserId;
  let customProperties = {};

  const visitedPathsCustomProperty = {
    ...getPersistedPathTrackCustomProperty(),
    ...getCurrentPathTrackCustomProperty(window.location.pathname),
  };

  if (!statsigUserId) {
    userId = (window as any).v4 ? (window as any).v4() : v4();
    setLocalStorage('statsigUserId', userId);
    setSessionStorage('@statsig:firstTouch', 'true');
  }

  if (getSessionStorage('@statsig:firstTouch') === 'true') {
    customProperties = {
      firstTouch: FIRST_TOUCH_TEXT,
    };
  }

  customProperties = {
    ...customProperties,
    ...visitedPathsCustomProperty,
  };

  const newClient = new StatsigClient(
    process.env.GATSBY_STATSIG_SDK_KEY || 'client-yMNyryucOI2ddIofgqohKtiJTW1PMsiovM05eN1gKDi',
    { userID: userId, custom: customProperties },
    holdEvents ? { loggingEnabled: 'disabled' } : undefined
  );

  // Store client for global access BEFORE initialization
  setStatsigClient(newClient, { holdEvents });

  // Hand statsigUserId to GA4 for cross-platform user identification: to a GA4
  // tag already on the page, and as statsig_user_id_ready for the container's
  // Data Layer Variable. A tag injected later reads the id from localStorage
  // itself.
  if (!holdEvents) {
    setGA4UserId(userId);
  }

  logPageLandedOncePerSession();

  return { client: newClient, userId };
};

const StatsigProviderMask: React.FC<any> = ({ children }) => {
  const [client, setClient] = useState<StatsigClient | null>(null);
  // Evaluated outside any StatsigProvider, so this is react-bindings' no-op
  // client, which resolves every experiment to its default.
  const noopClient = useContext(StatsigContext).client;

  // Statsig fetches the visitor's assignments, persists an anonymous ID, and
  // logs exposures. In an opt-in region it exists only once the visitor has
  // granted Performance cookies, and until then every experiment resolves to
  // its default. Under every other consent model (opt-out, notice only), where
  // every category is active from the first page load, it is created as soon
  // as OneTrust has loaded so the first page view renders the visitor's
  // variant, and it sends its events at once. It holds them instead when the
  // browser sends Global Privacy Control, and from an answer without
  // Performance on, until Performance is granted.
  //
  // Until the assignment is known, experiments stay in their loading state
  // (bounded by a timeout), so a page-level experiment never paints the control
  // and then replaces it with the variant.
  useIsomorphicLayoutEffect(() => {
    // Before subscribing, in case consent is already granted and fires at once.
    snapshotLandingOncePerSession();

    // One client per mount: whichever signal arrives first creates it, and the
    // other one only releases held events, since both can fire on one page.
    let created: { client: StatsigClient; userId: string; held: boolean; early: boolean } | null = null;

    const release = (): void => {
      if (!created?.held) return;
      created.held = false;
      // The ID goes first so the released events carry it in GA4.
      setGA4UserId(created.userId);
      releaseStatsigEvents();
    };

    // The scriptless build never loads OneTrust, so it has no decision to wait
    // for: experiments resolve to their defaults at once.
    const answeredBefore = bannerAnsweredBefore();
    if (isScriptless) {
      markStatsigNoClient();
    } else {
      markStatsigAwaitingConsent();
    }

    const unsubscribeGranted = onConsentGranted(CONSENT_CATEGORIES.performance, () => {
      if (created) {
        release();
        return;
      }
      created = { ...createStatsigClient(), held: false, early: false };
      setClient(created.client);
    });

    // An answer without Performance is the opt-out for the early client, which
    // was sending before any answer; a later grant on the same page releases it
    // again. A client created on a grant is not held when Performance is later
    // withdrawn. The first answer arrives through onConsentDecided, because
    // declining leaves hasConsent false and onConsentChanged reports only
    // changes of it.
    const onEarlyClientAnswer = (granted: boolean): void => {
      if (!created?.early) return;
      if (granted) {
        release();
      } else if (!created.held) {
        created.held = true;
        holdStatsigEvents();
      }
    };
    const unsubscribeFirstAnswer = onConsentDecided(CONSENT_CATEGORIES.performance, onEarlyClientAnswer);
    const unsubscribeChanged = onConsentChanged(CONSENT_CATEGORIES.performance, onEarlyClientAnswer);

    // Only a visitor who has not answered yet gets the early client, outside
    // the opt-in model and only while Performance is active. Once the visitor
    // has answered, their choice is the authority: onConsentGranted creates the
    // client for a grant, and a decline (or Performance switched off before any
    // answer) creates none. Either way the model settles a first-time visitor's
    // wait: with a client, or with the defaults. The condition is
    // firstPartyAnalyticsAllowed's rule for the time before an answer, composed
    // here because Global Privacy Control still creates a client, holding its
    // events; a change to either needs the same change in the other.
    const unsubscribeModel = onConsentModelKnown((model) => {
      if (
        model === CONSENT_MODELS.optIn ||
        created ||
        bannerAnsweredBefore() ||
        !categoryActive(CONSENT_CATEGORIES.performance)
      ) {
        markStatsigNoClient();
        return;
      }
      const holdEvents = globalPrivacyControl();
      created = { ...createStatsigClient({ holdEvents }), held: holdEvents, early: true };
      setClient(created.client);
    });

    // A visitor who answered the banner earlier has no assignment to show until
    // OneTrust reports their choice; a first-time visitor has none until it
    // reports the consent model. Experiments wait for that (bounded by a
    // timeout) instead of rendering the control first.
    let unsubscribeDecided = (): void => undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let afterLoadTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let unmounted = false;
    if (answeredBefore) {
      unsubscribeDecided = onConsentDecided(CONSENT_CATEGORIES.performance, (granted) => {
        if (!granted) {
          markStatsigNoClient();
        }
      });
      timeoutId = setTimeout(markStatsigNoClient, CONSENT_DECISION_TIMEOUT_MS);
    } else if (!isScriptless) {
      // The 2 s cap starts once the window has loaded, which is when OneTrust
      // is injected. The 5 s timer from mount covers the case where the load
      // or OneTrust never arrives; it bounds only the wait for the consent
      // model, not the early client's initialize that may follow.
      timeoutId = setTimeout(markStatsigNoClient, CONSENT_DECISION_TIMEOUT_MS);
      windowIsLoaded().then(() => {
        if (unmounted) return;
        afterLoadTimeoutId = setTimeout(markStatsigNoClient, FIRST_VISIT_DECISION_TIMEOUT_MS);
      });
    }

    return () => {
      unsubscribeGranted();
      unsubscribeFirstAnswer();
      unsubscribeChanged();
      unsubscribeModel();
      unsubscribeDecided();
      unmounted = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (afterLoadTimeoutId) clearTimeout(afterLoadTimeoutId);
    };
  }, []);

  // The provider is always rendered so the tree keeps one shape: swapping the
  // client prop updates context in place instead of remounting the page.
  return <StatsigProvider client={(client ?? noopClient) as any}>{children}</StatsigProvider>;
};

export default StatsigProviderMask;
